import { useState, useEffect, useCallback, useRef } from "react";
import { format, subDays } from "date-fns";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { XPBoard } from "./leaderboard/LeaderboardTable";
import { RoleLeaderboard } from "./leaderboard/RoleLeaderboard";
import { RepInsightsDrawer } from "./leaderboard/RepInsightsDrawer";
import { MyStatsCard } from "./leaderboard/MyStatsCard";
import { SyncPill } from "../components/SyncPill";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Diff the new leaderboard order against the snapshot in `prevRankRef`,
 * flag any user whose rank improved as "fanfare", flag the user that just
 * took #1 (if it changed) as "crown" + fire a confetti burst.
 *
 * Skipped on the very first load (no snapshot yet) so we don't celebrate
 * the initial page render.
 */
function applyLeaderboardWithFanfare(newData, prevRankRef, setRisingUsers) {
  const newRankMap = new Map(newData.map((rep, idx) => [rep.user_id, idx]));
  const newTopUserId = newData[0]?.user_id;
  const prev = prevRankRef.current;
  prevRankRef.current = { rankMap: newRankMap, topUserId: newTopUserId };

  // First load — no celebrations.
  if (!prev) return;

  const rising = {};
  newData.forEach((rep, idx) => {
    const prevIdx = prev.rankMap.get(rep.user_id);
    // Skip new entrants & no-movers; only celebrate genuine rank-ups.
    if (prevIdx !== undefined && idx < prevIdx) {
      rising[rep.user_id] = "fanfare";
    }
  });

  // New #1 — upgrade to crown + fire confetti.
  if (newTopUserId && newTopUserId !== prev.topUserId) {
    rising[newTopUserId] = "crown";
    fireConfettiBurst();
  }

  if (Object.keys(rising).length === 0) return;
  setRisingUsers(rising);
  // Clear after the longest animation finishes so the rows can re-fanfare on
  // a subsequent refetch.
  window.setTimeout(() => setRisingUsers({}), 1900);
}

function fireConfettiBurst() {
  // Two side-symmetric bursts angled inward — feels celebratory without
  // covering the table.
  const defaults = {
    spread: 70,
    startVelocity: 45,
    decay: 0.92,
    ticks: 180,
    gravity: 0.9,
    scalar: 0.9,
    colors: ["#10b981", "#facc15", "#ffffff", "#34d399", "#fde047"],
    zIndex: 9999,
  };
  confetti({ ...defaults, particleCount: 70, angle: 60, origin: { x: 0, y: 0.25 } });
  confetti({ ...defaults, particleCount: 70, angle: 120, origin: { x: 1, y: 0.25 } });
}

export default function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [xpBoard, setXpBoard] = useState([]);
  const [viewMode, setViewMode] = useState("by_role");
  const [dateRange, setDateRange] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [selectedRep, setSelectedRep] = useState(null);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  // Map<user_id, "fanfare" | "crown"> — rows currently celebrating a rank-up.
  // Cleared automatically after the CSS animation completes (~1.8s).
  const [risingUsers, setRisingUsers] = useState({});
  // Account-pinned custom leaderboard columns — surfaced inside the rep
  // insights drawer (RepInsightsDrawer) even though the All-Up board UI
  // is gone in Iter160f.
  const [customColumns, setCustomColumns] = useState([]);
  // Snapshot of the previous ranking so we can diff each refetch. Stored as
  // Map<user_id, rank> + the user_id at #1. Lives in a ref so it doesn't
  // trigger re-renders.
  const prevRankRef = useRef(null);

  const fetchCustomColumns = useCallback(async () => {
    try {
      const res = await fetch(`${API}/leaderboard/config`, { credentials: "include" });
      if (res.ok) {
        const { custom_columns } = await res.json();
        setCustomColumns(custom_columns || []);
      }
    } catch (e) { console.error("Failed to fetch leaderboard config:", e); }
  }, []);

  const fetchXpBoard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/gamification/leaderboard`, { credentials: "include" });
      if (res.ok) setXpBoard(await res.json());
    } catch (e) { console.error("Failed to fetch XP board:", e); }
  }, []);

  // We still fetch the All-Up leaderboard data in the background because
  // `MyStatsCard` (the rep's personal "your rank" widget) reads from it.
  // The All-Up *UI* (Iter160f) is removed but the data feed remains.
  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      // Iter185 (B06) — role dropdown removed; the by-role tabs are the
      // canonical role filter. All-Up feed is fetched unfiltered so the
      // MyStatsCard widget can compute rank across the whole account.
      const params = new URLSearchParams();
      params.append("start_date", format(dateRange.from, "yyyy-MM-dd"));
      if (dateRange.to) params.append("end_date", format(dateRange.to, "yyyy-MM-dd"));
      const response = await fetch(`${API}/leaderboard?${params}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        applyLeaderboardWithFanfare(data, prevRankRef, setRisingUsers);
        setLeaderboard(data);
        setLastSyncedAt(Date.now());
      }
    } catch (error) { console.error("Failed to fetch leaderboard:", error); }
    finally { setLoading(false); }
  }, [dateRange]);

  useEffect(() => {
    // Reset the rank snapshot whenever the date scope changes — the
    // "previous" ranks under a different filter aren't meaningful.
    prevRankRef.current = null;
    setRisingUsers({});
    fetchLeaderboard();
    fetchXpBoard();
    fetchCustomColumns();
  }, [dateRange, fetchLeaderboard, fetchXpBoard, fetchCustomColumns]);

  // Live mode — auto-refetch every 60s while the tab is visible so reps see
  // newly-closed deals on their teammates' wall-of-fame without a manual
  // reload. Pauses on background tabs.
  const handleRefresh = useCallback(() => {
    fetchLeaderboard();
    fetchXpBoard();
  }, [fetchLeaderboard, fetchXpBoard]);

  useAutoRefresh(handleRefresh, { intervalMs: 60_000 });

  const openInsights = (rep) => { setSelectedRep(rep); setInsightsOpen(true); };
  const { user: currentUser } = useAuth();
  const canCustomise = ["manager", "admin", "owner", "superadmin"].includes((currentUser?.role || "").toLowerCase());

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "Closer": return "badge-closer";
      case "Setter": return "badge-setter";
      case "Manager": return "badge-manager";
      case "DM Setter": return "badge-dm";
      default: return "bg-gray-500/10 text-gray-400";
    }
  };

  const getRoleBadgeLetter = (role) => {
    switch (role) {
      case "Closer": return "C";
      case "Setter": return "S";
      case "Manager": return "M";
      case "DM Setter": return "DM";
      default: return "?";
    }
  };

  const teamAverages = {
    dials: leaderboard.length > 0 ? Math.round(leaderboard.reduce((sum, r) => sum + r.dials, 0) / leaderboard.length) : 0,
    connects: leaderboard.length > 0 ? Math.round(leaderboard.reduce((sum, r) => sum + r.connects, 0) / leaderboard.length) : 0,
    sets: leaderboard.length > 0 ? Math.round(leaderboard.reduce((sum, r) => sum + r.sets, 0) / leaderboard.length) : 0,
    shows: leaderboard.length > 0 ? Math.round(leaderboard.reduce((sum, r) => sum + r.shows, 0) / leaderboard.length) : 0,
    deals: leaderboard.length > 0 ? Math.round(leaderboard.reduce((sum, r) => sum + r.deals, 0) / leaderboard.length) : 0,
    cash: leaderboard.length > 0 ? Math.round(leaderboard.reduce((sum, r) => sum + r.cash, 0) / leaderboard.length) : 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-yellow-400/20 flex items-center justify-center">
            <Trophy className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white font-heading">Leaderboard</h1>
            <p className="text-gray-400">Team performance rankings</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SyncPill
            lastSyncedAt={lastSyncedAt}
            loading={loading}
            onRefresh={handleRefresh}
            testId="leaderboard-sync-pill"
            live
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="date-range-btn" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(dateRange.from, "MMM d")} - {dateRange.to ? format(dateRange.to, "MMM d, yyyy") : ""}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-white/10" align="end">
              <Calendar mode="range" selected={dateRange} onSelect={(range) => range?.from && setDateRange(range)}
                numberOfMonths={2} className="bg-[#1A1A1A] text-white" />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Iter185 (B06) — role dropdown removed. The by-role tabs
            below (Closers / Phone Setters / DM Setters) already filter
            by role, so the extra dropdown was doing the same job twice
            and confusing users. */}
        <span className="text-sm text-gray-400">Filter by:</span>
        {/* Iter160 — quick-range presets so reps can flip between live
            today / 7d / 30d without opening the calendar popover. */}
        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-md p-0.5" data-testid="leaderboard-quick-range">
          {[
            { key: "today", label: "Today", days: 0 },
            { key: "7d", label: "7d", days: 6 },
            { key: "30d", label: "30d", days: 29 },
          ].map((preset) => {
            const today = new Date();
            const from = preset.days === 0 ? today : subDays(today, preset.days);
            const fromMatch = format(dateRange.from, "yyyy-MM-dd") === format(from, "yyyy-MM-dd");
            const toMatch = dateRange.to && format(dateRange.to, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");
            const active = fromMatch && toMatch;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => setDateRange({ from, to: today })}
                data-testid={`quick-range-${preset.key}`}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  active
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* My Stats Card — only renders for non-manager reps when they're on the board. */}
      <MyStatsCard
        currentUser={currentUser}
        leaderboard={leaderboard}
        loading={loading}
      />

      {/* View Toggle — Iter160f: All-Up tab removed; the by-role boards
          (Closers / Phone Setters / DM Setters) are now the canonical
          leaderboard surface. XP & Badges remains as a separate gamified
          view. */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="by_role" className="data-[state=active]:bg-white/10 text-sm" data-testid="tab-by-role">By Role</TabsTrigger>
          <TabsTrigger value="xp" className="data-[state=active]:bg-white/10 text-sm" data-testid="tab-xp">XP & Badges</TabsTrigger>
        </TabsList>
        <TabsContent value="by_role" className="mt-4">
          <RoleLeaderboard
            startDate={dateRange.from}
            endDate={dateRange.to}
            isManager={canCustomise}
          />
        </TabsContent>
        <TabsContent value="xp" className="mt-4">
          <XPBoard xpBoard={xpBoard} />
        </TabsContent>
      </Tabs>

      <RepInsightsDrawer open={insightsOpen} onOpenChange={setInsightsOpen}
        selectedRep={selectedRep} teamAverages={teamAverages} getRoleBadgeClass={getRoleBadgeClass}
        customColumns={customColumns} />
    </div>
  );
}
